import { useEffect, useState } from 'preact/hooks';
import { API, api, onEntry, type Account, type Info } from './api.js';
import { wallet, onWalletChange, accountIndexes, accountKey, notesZat, setSiteInfo } from './wallet.js';
import type { SignedRequest, SystemEvent } from '../../src/core/types.js';

/** One of this wallet's accounts: 0 is the main account, the rest trade one coin each. */
export interface MyAccount {
  index: number;
  publicKey: string;
  data: Account;
}

/** Tiny global store: platform info, ZEC price, and the signed-in wallet's accounts. */
interface State {
  info: Info | null;
  zecUsd: number | null;
  account: Account | null; // main account
  accounts: MyAccount[];
}
const state: State = { info: null, zecUsd: null, account: null, accounts: [] };
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function useStore(): State {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((x) => x + 1);
    subs.add(f);
    const off = onWalletChange(f);
    return () => {
      subs.delete(f);
      off();
    };
  }, []);
  return state;
}

/** Everything the wallet owns in ZEC: all accounts plus unspent notes. */
export function totalZat(): bigint {
  return state.accounts.reduce((s, a) => s + BigInt(a.data.zat ?? '0'), 0n) + notesZat();
}

export function accountZat(index: number): bigint {
  return BigInt(state.accounts.find((a) => a.index === index)?.data.zat ?? '0');
}

/** Coin accounts' free ZEC (not main, not notes). */
export function coinAccountsZat(): bigint {
  return state.accounts.filter((a) => a.index !== 0).reduce((s, a) => s + BigInt(a.data.zat ?? '0'), 0n);
}

export function myKeys(): Set<string> {
  return new Set(state.accounts.map((a) => a.publicKey));
}

/** This wallet's balances of one coin, largest first. */
export function holdingsOf(tokenId: string): { index: number; balance: bigint }[] {
  return state.accounts
    .map((a) => ({ index: a.index, balance: BigInt(a.data.tokens?.find((t) => t.id === tokenId)?.balance ?? '0') }))
    .filter((h) => h.balance > 0n)
    .sort((a, b) => (b.balance > a.balance ? 1 : -1));
}

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
export function refreshAccount(delay = 0) {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(async () => {
    if (!wallet()) {
      state.account = null;
      state.accounts = [];
      emit();
      return;
    }
    try {
      const idx = accountIndexes();
      const info = await api.info();
      state.info = info; // keeps platform stats (notes in circulation) current
      setSiteInfo(info);
      // Accounts one at a time with random pauses: fetching them all in one burst would show
      // anyone watching the server's traffic which accounts belong to one wallet.
      const data: (typeof state.accounts)[number]['data'][] = [];
      for (const [j, i] of idx.entries()) {
        if (j > 0) await new Promise((r) => setTimeout(r, 200 + Math.random() * 1300));
        data.push(await api.account(accountKey(i)));
      }
      state.accounts = idx.map((index, j) => ({ index, publicKey: accountKey(index), data: data[j] })).filter((a) => a.index === 0 || a.data.exists);
      state.account = data[0];
      emit();
    } catch {
      /* keep last */
    }
  }, delay);
}

export async function boot() {
  try {
    state.info = await api.info();
    setSiteInfo(state.info);
  } catch {
    /* shown as offline */
  }
  emit();
  refreshAccount();
  const loadPrice = () =>
    fetch(API + '/api/price')
      .then((r) => r.json())
      .then((j) => {
        state.zecUsd = j.zecUsd ?? null;
        emit();
      })
      .catch(() => {});
  void loadPrice();
  setInterval(loadPrice, 120_000);
  onWalletChange(() => refreshAccount(50));

  onEntry((e) => {
    if (state.info) state.info.seq = e.seq;
    if (!wallet()) return;
    const mine = myKeys();
    let hit = false;
    if (e.kind === 'user') hit = mine.has((e.data as SignedRequest).req.user);
    else if (e.kind === 'system') {
      const ev = e.data as SystemEvent;
      if ('user' in ev) hit = mine.has(ev.user);
      if (ev.type === 'withdraw_sent' || ev.type === 'withdraw_failed') hit = state.accounts.some((a) => a.data.pending?.some((p) => p.id === ev.id));
    }
    // Creator fees and the like change balances without our own entry; a cheap periodic refresh covers those.
    if (hit) refreshAccount(150);
  });
  setInterval(() => refreshAccount(), 30_000);
}
